import { Container } from 'pixi.js';
import { xOf, yOf } from '../sim/constants';
import type { Terrain } from '../sim/types';
import type { GrayboxTextures } from './graybox';
import { CHUNKS_X, CHUNKS_Y, CHUNK_SIZE, bakeChunk } from './ChunkBaker';

const MAX_REBAKES_PER_FRAME = 2;

export class TerrainLayer {
  readonly container = new Container();
  private readonly chunks: Container[] = [];
  private readonly dirty = new Set<number>();
  private readonly tex: GrayboxTextures;

  constructor(tex: GrayboxTextures, terrain: Terrain) {
    this.tex = tex;
    // Row-major chunk order keeps painter's order correct across chunk seams.
    for (let cy = 0; cy < CHUNKS_Y; cy += 1) {
      for (let cx = 0; cx < CHUNKS_X; cx += 1) {
        const chunk = new Container();
        chunk.cullable = true;
        this.chunks.push(chunk);
        this.container.addChild(chunk);
        bakeChunk(chunk, cx, cy, terrain, this.tex);
        chunk.cacheAsTexture(true);
      }
    }
  }

  /**
   * Mark the chunk owning `col` dirty, plus neighbors whose baked content
   * depends on it: west/north faces hang over this column, and east/south
   * road/pipe connection stubs point into it.
   */
  markTileDirty(col: number): void {
    const x = xOf(col);
    const y = yOf(col);
    const cx = Math.floor(x / CHUNK_SIZE);
    const cy = Math.floor(y / CHUNK_SIZE);
    this.dirty.add(cy * CHUNKS_X + cx);
    if (x % CHUNK_SIZE === 0 && cx > 0) this.dirty.add(cy * CHUNKS_X + cx - 1);
    if (y % CHUNK_SIZE === 0 && cy > 0) this.dirty.add((cy - 1) * CHUNKS_X + cx);
    if (x % CHUNK_SIZE === CHUNK_SIZE - 1 && cx < CHUNKS_X - 1) this.dirty.add(cy * CHUNKS_X + cx + 1);
    if (y % CHUNK_SIZE === CHUNK_SIZE - 1 && cy < CHUNKS_Y - 1) this.dirty.add((cy + 1) * CHUNKS_X + cx);
  }

  markAllDirty(): void {
    for (let index = 0; index < this.chunks.length; index += 1) this.dirty.add(index);
  }

  update(terrain: Terrain): void {
    let budget = MAX_REBAKES_PER_FRAME;
    for (const index of this.dirty) {
      if (budget <= 0) break;
      const chunk = this.chunks[index]!;
      chunk.cacheAsTexture(false);
      bakeChunk(chunk, index % CHUNKS_X, Math.floor(index / CHUNKS_X), terrain, this.tex);
      chunk.cacheAsTexture(true);
      this.dirty.delete(index);
      budget -= 1;
    }
  }
}
